import { Component } from '@theme/component';
import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/*
 * ArtGalerie photo viewer.
 *
 * Adapted from Horizon's zoom dialog. The element name, the public open() / close() /
 * loadHighResolutionImage() methods and the zoom-media:selected event stay the same, so
 * media-gallery.js and the gallery triggers work unchanged. Markup: snippets/ag-viewer.liquid,
 * styles: assets/ag-viewer.css.
 *
 * Opens at the tapped photo with the whole image visible, moves sideways (swipe, arrows,
 * keyboard, thumbnails), zooms only on request (double tap, click, pinch, zoom button) and
 * closes with the close button, Escape, a click beside the photo (mouse) or a swipe down (touch).
 */

const ZOOM_SCALE = 2.5;
const MAX_SCALE = 4;
const TAP_SLOP = 10;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP = 36;
const SWIPE_RATIO = 0.16;
const FLICK_SPEED = 0.3;
const DISMISS_DISTANCE = 110;
const DISMISS_SPEED = 0.5;
const EDGE_RESISTANCE = 0.32;
const SLIDE_MS = 340;
const ZOOM_MS = 280;
const CLOSE_MS = 170;
const STYLES_TIMEOUT_MS = 6000;
const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const INTERACTIVE_MEDIA = 'deferred-media, product-model, video, iframe, model-viewer, button, a';
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} stage - The area that receives the gestures.
 * @property {HTMLElement} track - The horizontal strip of slides.
 * @property {HTMLElement[]} media - One slide per product media, in gallery order.
 * @property {HTMLElement} [thumbs] - Thumbnail strip (dots on phones).
 * @property {HTMLElement[]} [thumbButtons] - Thumbnail buttons.
 * @property {HTMLElement} [counterVisible] - Visible "2 / 5" counter.
 * @property {HTMLElement} [counterText] - Screen reader counter text.
 * @property {HTMLButtonElement} [zoomButton] - Zoom toggle.
 * @property {HTMLButtonElement} [prevButton] - Previous photo.
 * @property {HTMLButtonElement} [nextButton] - Next photo.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'stage', 'track', 'media'];

  #index = 0;
  #isOpen = false;
  #closing = false;
  /** @type {HTMLElement | null} */
  #opener = null;
  #scrollTop = 0;
  #scale = 1;
  #tx = 0;
  #ty = 0;
  #width = 0;
  #gap = 0;
  /** @type {Map<number, {x: number, y: number}>} */
  #pointers = new Map();
  /** @type {any} */
  #gesture = null;
  /** @type {{t: number, x: number, y: number} | null} */
  #lastTap = null;
  /** @type {AbortController | null} */
  #session = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #wheelUntil = 0;
  #wheelSum = 0;
  #wheelLast = 0;
  /** @type {Set<number>} */
  #timers = new Set();
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  #waitingForStyles = false;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog = this.refs.dialog;
    this.#dialog.addEventListener('cancel', this.#onCancel);
    this.#dialog.addEventListener('close', this.#onNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialog?.removeEventListener('cancel', this.#onCancel);
    this.#dialog?.removeEventListener('close', this.#onNativeClose);
    if (this.#isOpen) this.#finishClose(false);
  }

  get #slides() {
    return this.refs.media || [];
  }

  get #count() {
    return this.#slides.length;
  }

  get #reduced() {
    return prefersReducedMotion();
  }

  /** The index of the photo currently shown. */
  get index() {
    return this.#index;
  }

  /**
   * Opens the viewer at a photo. Called by the gallery triggers.
   * @param {number | string} index - The index of the media to show.
   * @param {Event} [event] - The triggering event.
   */
  open(index, event) {
    event?.preventDefault?.();

    const { dialog } = this.refs;
    if (!dialog || dialog.open || !this.#count || this.#waitingForStyles) return;

    const opener = findOpener(event);

    // ag-viewer.css loads without blocking the page. On a slow connection a photo can be tapped
    // before it has arrived: wait for it instead of showing the photos unstyled, one below the other.
    if (!this.#styled()) {
      this.#waitingForStyles = true;
      waitFor(() => this.#styled(), STYLES_TIMEOUT_MS).then(() => {
        this.#waitingForStyles = false;
        if (this.isConnected && !dialog.open) this.#show(index, opener);
      });
      return;
    }

    this.#show(index, opener);
  }

  /** Whether assets/ag-viewer.css is applied. */
  #styled() {
    return getComputedStyle(this.refs.stage).position === 'absolute';
  }

  /**
   * @param {number | string} index
   * @param {HTMLElement | null} opener
   */
  #show(index, opener) {
    const { dialog } = this.refs;

    this.#index = clamp(Math.round(Number(index)) || 0, 0, this.#count - 1);
    this.#opener = opener;
    this.#closing = false;
    this.#scrollTop = getScrollTop();
    this.#clearTimers();
    dialog.classList.remove('is-closing', 'is-dismissing', 'is-zoomed', 'is-gesturing', 'is-panning');
    dialog.style.removeProperty('--ag-v-bg-alpha');
    dialog.style.removeProperty('--ag-v-ui-alpha');

    lockScroll(dialog);
    dialog.showModal();
    this.#isOpen = true;

    if (!this.#reduced) {
      dialog.classList.add('is-opening');
      this.#later(() => dialog.classList.remove('is-opening'), 360);
    }

    this.#bind();
    this.#measure();
    this.#resetZoom(false);
    this.#resetFrames(false);
    this.#placeTrack(0, false);
    this.#sync(true);
    dialog.focus({ preventScroll: true });
  }

  /** Closes the viewer. */
  close() {
    return this.#close();
  }

  /** Shows the next photo. */
  next() {
    this.go(this.#index + 1);
  }

  /** Shows the previous photo. */
  previous() {
    this.go(this.#index - 1);
  }

  /**
   * Shows the photo at an index (clamped to the first and last photo).
   * @param {number} index - The index to show.
   * @param {boolean} [animate] - Whether to slide.
   */
  go(index, animate = true) {
    if (!this.#count) return;

    const target = clamp(index, 0, this.#count - 1);
    const changed = target !== this.#index;

    if (changed) {
      this.#resetZoom(false);
      this.#lastTap = null;
    }

    this.#index = target;
    this.#placeTrack(0, animate);

    if (changed) {
      this.#sync(false);
      this.dispatchEvent(new ZoomMediaSelectedEvent(target));
    }
  }

  /**
   * Warms up the photo of a slide before the viewer opens (gallery hover).
   * Kept for Horizon's media-gallery.js.
   * @param {HTMLElement} slide - A slide of this viewer.
   */
  loadHighResolutionImage(slide) {
    const index = this.#slides.indexOf(slide);
    if (index < 0) return false;

    this.#prime(index, false);
    return true;
  }

  /* Opening and closing */

  /**
   * @param {boolean} [fromDismiss] - Closing after a swipe down.
   */
  async #close(fromDismiss = false) {
    if (!this.#isOpen || this.#closing) return;
    this.#closing = true;

    const { dialog, stage } = this.refs;

    if (!this.#reduced) {
      if (fromDismiss) {
        const frame = this.#currentFrame();
        if (frame) {
          const current = new DOMMatrixReadOnly(getComputedStyle(frame).transform).m42;
          frame.style.transition = `transform ${CLOSE_MS}ms ease-in`;
          frame.style.transform = `translate3d(0, ${current + stage.clientHeight * 0.35}px, 0) scale(0.8)`;
        }
      }

      dialog.classList.add('is-closing');
      await new Promise((resolve) => this.#later(resolve, CLOSE_MS));
    }

    if (this.#isOpen) this.#finishClose(true);
  }

  /**
   * @param {boolean} restoreFocus - Return focus to the photo that opened the viewer.
   */
  #finishClose(restoreFocus) {
    const dialog = this.#dialog;

    this.#isOpen = false;
    this.#closing = false;
    this.#clearTimers();
    this.#unbind();
    this.#pointers.clear();
    this.#gesture = null;
    this.#lastTap = null;

    if (dialog) {
      if (dialog.open) dialog.close();
      dialog.classList.remove('is-closing', 'is-opening', 'is-dismissing', 'is-zoomed', 'is-gesturing', 'is-panning');
      dialog.style.removeProperty('--ag-v-bg-alpha');
      dialog.style.removeProperty('--ag-v-ui-alpha');
      unlockScroll(dialog);
    }

    this.#resetZoom(false);
    this.#resetFrames(false);

    if (Math.abs(getScrollTop() - this.#scrollTop) > 1) {
      scrollTo({ top: this.#scrollTop, behavior: 'instant' });
    }

    window.dispatchEvent(new DialogCloseEvent());

    if (restoreFocus) this.#restoreFocus();
  }

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #onNativeClose = () => {
    if (this.#isOpen) this.#finishClose(true);
  };

  #restoreFocus() {
    let target = this.#opener;
    this.#opener = null;

    // In the carousel the gallery follows the viewer, so focus the photo now on screen.
    if (target?.closest('slideshow-component')) {
      const slides = this.closest('media-gallery')?.querySelectorAll('slideshow-slide');
      const current = slides?.[this.#index];
      if (current instanceof HTMLElement) target = current;
    }

    if (!target || !target.isConnected) return;

    if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) {
      const element = target;
      element.setAttribute('tabindex', '-1');
      element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
    }

    target.focus({ preventScroll: true });
  }

  #bind() {
    this.#unbind();
    this.#session = new AbortController();

    const { signal } = this.#session;
    const { dialog, stage } = this.refs;

    stage.addEventListener('pointerdown', this.#onPointerDown, { signal });
    stage.addEventListener('pointermove', this.#onPointerMove, { signal });
    stage.addEventListener('pointerup', this.#onPointerUp, { signal });
    stage.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    stage.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    stage.addEventListener('touchmove', preventDefault, { signal, passive: false });
    stage.addEventListener('dragstart', preventDefault, { signal });
    dialog.addEventListener('gesturestart', preventDefault, { signal });
    dialog.addEventListener('keydown', this.#onKeyDown, { signal });
    dialog.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('resize', this.#onResize, { signal });

    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(stage);
  }

  #unbind() {
    this.#session?.abort();
    this.#session = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /* Layout */

  #measure() {
    const { stage, track } = this.refs;
    this.#width = stage.clientWidth;
    this.#gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  }

  #onResize = () => {
    if (!this.#isOpen) return;

    this.#measure();

    if (this.#gesture?.type !== 'swipe') this.#placeTrack(0, false);
    if (this.#scale > 1.01 && !this.#gesture) this.#settleZoom(false, null);

    this.#prime(this.#index, true);
  };

  /** @param {number} [offset] */
  #trackX(offset = 0) {
    return -this.#index * (this.#width + this.#gap) + offset;
  }

  /**
   * @param {number} offset - Pixels away from the resting position of the current slide.
   * @param {boolean} animate - Slide there instead of jumping.
   */
  #placeTrack(offset, animate) {
    const { track } = this.refs;
    track.style.transition = animate && !this.#reduced ? `transform ${SLIDE_MS}ms ${EASE}` : 'none';
    track.style.transform = `translate3d(${this.#trackX(offset)}px, 0, 0)`;
  }

  /** Stops a running slide animation where it is and returns its offset. */
  #freezeTrack() {
    const { track } = this.refs;
    if (!track.style.transition || track.style.transition === 'none') return 0;

    const x = new DOMMatrixReadOnly(getComputedStyle(track).transform).m41;
    const offset = x - this.#trackX(0);
    if (Math.abs(offset) < 0.5) return 0;

    this.#placeTrack(offset, false);
    return offset;
  }

  /**
   * Updates slides, counter, buttons and thumbnails for the current photo.
   * @param {boolean} opening - First update after opening.
   */
  #sync(opening) {
    const index = this.#index;
    const count = this.#count;

    this.#slides.forEach((slide, n) => {
      const current = n === index;
      slide.classList.toggle('is-current', current);
      slide.toggleAttribute('inert', !current);
      if (current) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });

    const { dialog, counterVisible, counterText, prevButton, nextButton, zoomButton, thumbButtons } = this.refs;

    if (counterVisible) counterVisible.textContent = `${index + 1} / ${count}`;
    if (counterText) {
      const template = dialog.dataset.counterTemplate || '[n] / [total]';
      counterText.textContent = template.replace('[n]', String(index + 1)).replace('[total]', String(count));
    }

    prevButton?.setAttribute('aria-disabled', String(index === 0));
    nextButton?.setAttribute('aria-disabled', String(index === count - 1));
    if (zoomButton) zoomButton.hidden = !this.#currentImage();

    thumbButtons?.forEach((button, n) => button.setAttribute('aria-current', String(n === index)));
    this.#revealThumb(opening);
    this.#updateZoomState();

    this.#prime(index, true);
    this.#later(() => {
      this.#prime(index + 1, false);
      this.#prime(index - 1, false);
    }, opening ? 150 : 60);
  }

  /** @param {boolean} instant */
  #revealThumb(instant) {
    const { thumbs, thumbButtons } = this.refs;
    const button = thumbButtons?.[this.#index];
    if (!thumbs || !button || thumbs.scrollWidth <= thumbs.clientWidth + 1) return;

    const left = button.offsetLeft - (thumbs.clientWidth - button.offsetWidth) / 2;
    thumbs.scrollTo({ left, behavior: instant || this.#reduced ? 'auto' : 'smooth' });
  }

  /* Images */

  /**
   * Gives a slide the photo size it needs: the fitted size, or larger while zoomed.
   * Until the sharp file arrives, the photo already loaded by the gallery is shown.
   * @param {number} index - The slide index.
   * @param {boolean} urgent - Load with high priority.
   */
  #prime(index, urgent) {
    const slide = this.#slides[index];
    const image = slide?.querySelector('img.ag-viewer__img');
    if (!(image instanceof HTMLImageElement)) return;

    const scale = index === this.#index ? Math.max(1, this.#scale) : 1;
    const needed = Math.ceil(this.#displayWidth(image, slide) * scale);
    const current = parseInt(image.getAttribute('sizes') || '0', 10) || 0;
    if (needed > current) image.setAttribute('sizes', `${needed}px`);

    if (!image.hasAttribute('srcset') && image.dataset.srcset) {
      if (!image.getAttribute('src')) {
        const placeholder = this.#galleryImage(index);
        if (placeholder) image.src = placeholder;
      }
      image.setAttribute('fetchpriority', urgent ? 'high' : 'low');
      image.setAttribute('srcset', image.dataset.srcset);
    }
  }

  /**
   * @param {HTMLImageElement} image
   * @param {HTMLElement} slide
   */
  #displayWidth(image, slide) {
    if (image.offsetWidth > 0) return image.offsetWidth;

    // Viewer not on screen yet: estimate from the window and the paddings in ag-viewer.css.
    const ratio = parseFloat(slide.style.getPropertyValue('--ag-r')) || 1;
    const phone = window.innerWidth < 750;
    const width = Math.max(1, window.innerWidth - (phone ? 0 : 224));
    const height = Math.max(1, window.innerHeight - (phone ? 152 : 184));
    return Math.min(width, height * ratio);
  }

  /**
   * The photo the gallery already shows for this index (used as a placeholder).
   * @param {number} index
   */
  #galleryImage(index) {
    const gallery = this.closest('media-gallery');
    if (!gallery) return '';

    for (const selector of ['.media-gallery__grid > li', 'slideshow-slide']) {
      const image = gallery.querySelectorAll(selector)[index]?.querySelector('img');
      if (image instanceof HTMLImageElement && image.complete && image.naturalWidth > 0 && image.currentSrc) {
        return image.currentSrc;
      }
    }

    return '';
  }

  #currentImage() {
    const image = this.#slides[this.#index]?.querySelector('img.ag-viewer__img');
    return image instanceof HTMLImageElement ? image : null;
  }

  #currentFrame() {
    const frame = this.#slides[this.#index]?.querySelector('.ag-viewer__frame');
    return frame instanceof HTMLElement ? frame : null;
  }

  /* Zoom */

  /** Resting geometry of the current photo, independent of running animations. */
  #geometry() {
    const image = this.#currentImage();
    const frame = image?.parentElement;
    if (!image || !frame || !image.offsetWidth) return null;

    const rect = this.refs.stage.getBoundingClientRect();
    return {
      rect,
      cx: rect.left + frame.offsetLeft + frame.offsetWidth / 2,
      cy: rect.top + frame.offsetTop + frame.offsetHeight / 2,
      w: image.offsetWidth,
      h: image.offsetHeight,
    };
  }

  /**
   * How far the zoomed photo may move so it always covers the screen where it can.
   * @param {number} scale
   * @param {{rect: DOMRect, cx: number, cy: number, w: number, h: number}} g
   */
  #bounds(scale, g) {
    const width = g.w * scale;
    const height = g.h * scale;
    const { rect } = g;
    const x = width <= rect.width ? [0, 0] : [rect.right - g.cx - width / 2, rect.left - g.cx + width / 2];
    const y = height <= rect.height ? [0, 0] : [rect.bottom - g.cy - height / 2, rect.top - g.cy + height / 2];
    return { minX: x[0], maxX: x[1], minY: y[0], maxY: y[1] };
  }

  /** @param {boolean} animate */
  #applyZoom(animate) {
    const image = this.#currentImage();
    if (image) {
      image.style.transition = animate && !this.#reduced ? `transform ${ZOOM_MS}ms ${EASE}` : 'none';
      image.style.transform =
        this.#scale === 1 && this.#tx === 0 && this.#ty === 0
          ? ''
          : `translate3d(${this.#tx}px, ${this.#ty}px, 0) scale(${this.#scale})`;
    }
    this.#updateZoomState();
  }

  #updateZoomState() {
    const zoomed = this.#scale > 1.01;
    this.refs.dialog.classList.toggle('is-zoomed', zoomed);
    this.refs.zoomButton?.setAttribute('aria-pressed', String(zoomed));
  }

  /** @param {boolean} animate */
  #resetZoom(animate) {
    this.#scale = 1;
    this.#tx = 0;
    this.#ty = 0;
    this.#applyZoom(animate);
  }

  /**
   * Zooms so that the point under (x, y) stays in place.
   * @param {number} scale
   * @param {number} x
   * @param {number} y
   * @param {boolean} [animate]
   */
  #zoomTo(scale, x, y, animate = true) {
    const g = this.#geometry();
    if (!g) return;

    const from = this.#scale;
    const halfWidth = (g.w * from) / 2;
    const halfHeight = (g.h * from) / 2;
    const fx = clamp(x, g.cx + this.#tx - halfWidth, g.cx + this.#tx + halfWidth);
    const fy = clamp(y, g.cy + this.#ty - halfHeight, g.cy + this.#ty + halfHeight);
    const to = clamp(scale, 1, MAX_SCALE);

    if (to <= 1.001) {
      this.#resetZoom(animate);
      return;
    }

    const b = this.#bounds(to, g);
    this.#tx = clamp(fx - g.cx - (to * (fx - g.cx - this.#tx)) / from, b.minX, b.maxX);
    this.#ty = clamp(fy - g.cy - (to * (fy - g.cy - this.#ty)) / from, b.minY, b.maxY);
    this.#scale = to;
    this.#applyZoom(animate);
    this.#prime(this.#index, true);
  }

  /**
   * @param {number} [x]
   * @param {number} [y]
   */
  #toggleZoom(x, y) {
    if (this.#scale > 1.01) {
      this.#resetZoom(true);
      return;
    }

    const g = this.#geometry();
    if (!g) return;
    this.#zoomTo(ZOOM_SCALE, x ?? g.cx, y ?? g.cy, true);
  }

  /**
   * Brings the zoom back into its limits after a pinch or a resize.
   * @param {boolean} animate
   * @param {{x: number, y: number} | null} focus
   */
  #settleZoom(animate, focus) {
    const g = this.#geometry();
    if (!g) return;

    if (this.#scale < 1.05) {
      this.#resetZoom(animate);
      return;
    }

    if (this.#scale > MAX_SCALE) {
      const point = focus || { x: g.cx + this.#tx, y: g.cy + this.#ty };
      this.#zoomTo(MAX_SCALE, point.x, point.y, animate);
      return;
    }

    const b = this.#bounds(this.#scale, g);
    this.#tx = clamp(this.#tx, b.minX, b.maxX);
    this.#ty = clamp(this.#ty, b.minY, b.maxY);
    this.#applyZoom(animate);
    this.#prime(this.#index, true);
  }

  /**
   * @param {number} directionX - -1, 0 or 1
   * @param {number} directionY - -1, 0 or 1
   */
  #panBy(directionX, directionY) {
    const g = this.#geometry();
    if (!g) return;

    const b = this.#bounds(this.#scale, g);
    this.#tx = clamp(this.#tx + directionX * g.rect.width * 0.2, b.minX, b.maxX);
    this.#ty = clamp(this.#ty + directionY * g.rect.height * 0.2, b.minY, b.maxY);
    this.#applyZoom(true);
  }

  /* Swipe down to close */

  /** @param {number} distanceY */
  #moveDismiss(distanceY) {
    const frame = this.#currentFrame();
    if (!frame) return;

    const { dialog, stage } = this.refs;
    const progress = Math.min(1, distanceY / ((stage.clientHeight || 1) * 0.5));

    frame.style.transition = 'none';
    frame.style.transform = `translate3d(0, ${distanceY}px, 0) scale(${1 - progress * 0.18})`;
    dialog.style.setProperty('--ag-v-bg-alpha', String(1 - progress * 0.9));
    dialog.style.setProperty('--ag-v-ui-alpha', String(Math.max(0, 1 - progress * 2.5)));
  }

  /**
   * @param {number} distanceY
   * @param {number} velocityY
   */
  #endDismiss(distanceY, velocityY) {
    this.refs.dialog.classList.remove('is-dismissing');

    if (distanceY > DISMISS_DISTANCE || (distanceY > 30 && velocityY > DISMISS_SPEED)) {
      this.#close(true);
      return;
    }

    this.#resetFrames(true);
  }

  /** @param {boolean} animate */
  #resetFrames(animate) {
    const dialog = this.#dialog;
    dialog?.style.removeProperty('--ag-v-bg-alpha');
    dialog?.style.removeProperty('--ag-v-ui-alpha');

    for (const frame of this.refs.track?.querySelectorAll('.ag-viewer__frame') || []) {
      if (!(frame instanceof HTMLElement) || !frame.style.transform) continue;
      frame.style.transition = animate && !this.#reduced ? `transform 260ms ${EASE}` : 'none';
      frame.style.transform = '';
    }
  }

  /* Pointer gestures */

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.#isOpen || this.#closing) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest(INTERACTIVE_MEDIA)) return;
    if (this.#pointers.size >= 2) return;

    try {
      this.refs.stage.setPointerCapture(event.pointerId);
    } catch {
      // The pointer may already be gone.
    }

    if (event.pointerType === 'mouse') event.preventDefault();

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size === 1) {
      this.#gesture = {
        type: 'pending',
        id: event.pointerId,
        pointer: event.pointerType,
        x0: event.clientX,
        y0: event.clientY,
        lx: event.clientX,
        ly: event.clientY,
        lt: event.timeStamp,
        vx: 0,
        vy: 0,
        offset: this.#freezeTrack(),
        tx0: this.#tx,
        ty0: this.#ty,
        geom: null,
      };
    } else {
      this.#startPinch(event.pointerType);
    }
  };

  /** @param {string} pointer */
  #startPinch(pointer) {
    const geom = this.#geometry();
    const previous = this.#gesture;
    if (!geom) return;

    if (previous?.type === 'swipe' || previous?.offset) this.#placeTrack(0, true);
    if (previous?.type === 'dismiss') {
      this.refs.dialog.classList.remove('is-dismissing');
      this.#resetFrames(true);
    }

    const [a, b] = [...this.#pointers.values()];
    if (!a || !b) return;

    this.#gesture = {
      type: 'pinch',
      pointer,
      d0: Math.max(1, distance(a, b)),
      m0: midpoint(a, b),
      m: midpoint(a, b),
      s0: this.#scale,
      tx0: this.#tx,
      ty0: this.#ty,
      geom,
    };
    this.#lastTap = null;
    this.refs.dialog.classList.add('is-gesturing');
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const point = this.#pointers.get(event.pointerId);
    const g = this.#gesture;
    if (!point || !g) return;

    point.x = event.clientX;
    point.y = event.clientY;

    if (g.type === 'pinch') {
      this.#movePinch();
      return;
    }

    if (event.pointerId !== g.id) return;

    const elapsed = event.timeStamp - g.lt;
    if (elapsed > 0) {
      g.vx = 0.7 * ((event.clientX - g.lx) / elapsed) + 0.3 * g.vx;
      g.vy = 0.7 * ((event.clientY - g.ly) / elapsed) + 0.3 * g.vy;
      g.lx = event.clientX;
      g.ly = event.clientY;
      g.lt = event.timeStamp;
    }

    const dx = event.clientX - g.x0;
    const dy = event.clientY - g.y0;

    if (g.type === 'pending') {
      if (Math.hypot(dx, dy) < TAP_SLOP) return;

      const { dialog } = this.refs;
      this.#lastTap = null;

      if (this.#scale > 1.01) {
        g.type = 'pan';
        g.geom = this.#geometry();
        dialog.classList.add('is-panning');
      } else if (Math.abs(dx) >= Math.abs(dy)) {
        g.type = 'swipe';
      } else if (dy > 0 && g.pointer !== 'mouse') {
        g.type = 'dismiss';
        dialog.classList.add('is-dismissing');
      } else {
        g.type = 'idle';
      }

      dialog.classList.add('is-gesturing');
    }

    if (g.type === 'swipe') {
      let offset = g.offset + dx;
      const atStart = this.#index === 0 && offset > 0;
      const atEnd = this.#index === this.#count - 1 && offset < 0;
      if (atStart || atEnd) offset *= EDGE_RESISTANCE;
      this.#placeTrack(offset, false);
    } else if (g.type === 'dismiss') {
      this.#moveDismiss(Math.max(0, dy));
    } else if (g.type === 'pan' && g.geom) {
      const b = this.#bounds(this.#scale, g.geom);
      this.#tx = rubber(g.tx0 + dx, b.minX, b.maxX);
      this.#ty = rubber(g.ty0 + dy, b.minY, b.maxY);
      this.#applyZoom(false);
    }
  };

  #movePinch() {
    const g = this.#gesture;
    const [a, b] = [...this.#pointers.values()];
    if (!a || !b) return;

    const m = midpoint(a, b);
    let scale = g.s0 * (distance(a, b) / g.d0);
    if (scale < 1) scale = Math.max(0.6, 1 - (1 - scale) * 0.45);
    if (scale > MAX_SCALE) scale = MAX_SCALE + (scale - MAX_SCALE) * 0.3;

    g.m = m;
    this.#scale = scale;
    this.#tx = m.x - g.geom.cx - (scale * (g.m0.x - g.geom.cx - g.tx0)) / g.s0;
    this.#ty = m.y - g.geom.cy - (scale * (g.m0.y - g.geom.cy - g.ty0)) / g.s0;
    this.#applyZoom(false);
  }

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => this.#endPointer(event, false);

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => this.#endPointer(event, true);

  /**
   * @param {PointerEvent} event
   * @param {boolean} cancelled
   */
  #endPointer(event, cancelled) {
    if (!this.#pointers.delete(event.pointerId)) return;

    const g = this.#gesture;
    if (!g) return;

    if (g.type === 'pinch') {
      const remaining = [...this.#pointers.entries()][0];

      if (remaining && this.#scale > 1.05 && this.#scale <= MAX_SCALE) {
        // Keep moving the zoomed photo with the finger that stays down.
        const [id, point] = remaining;
        this.#gesture = {
          type: 'pan',
          id,
          pointer: g.pointer,
          x0: point.x,
          y0: point.y,
          lx: point.x,
          ly: point.y,
          lt: event.timeStamp,
          vx: 0,
          vy: 0,
          offset: 0,
          tx0: this.#tx,
          ty0: this.#ty,
          geom: g.geom,
        };
        this.refs.dialog.classList.add('is-panning');
        return;
      }

      this.#settleZoom(true, g.m);

      if (remaining) {
        this.#gesture = { type: 'idle', id: remaining[0], pointer: g.pointer, offset: 0 };
      } else {
        this.#endGesture();
      }
      return;
    }

    if (event.pointerId !== g.id) return;

    if (event.timeStamp - g.lt > 90) {
      g.vx = 0;
      g.vy = 0;
    }

    const dx = event.clientX - g.x0;
    const dy = event.clientY - g.y0;

    switch (g.type) {
      case 'pending':
        if (g.offset) this.#placeTrack(0, true);
        if (!cancelled) this.#tap(event, g);
        break;
      case 'swipe':
        if (cancelled) this.#endSwipe(0, 0, 0);
        else this.#endSwipe(g.offset + dx, g.vx, dx);
        break;
      case 'dismiss':
        this.#endDismiss(cancelled ? 0 : dy, cancelled ? 0 : g.vy);
        break;
      case 'pan':
        this.#endPan(g);
        break;
      default:
        if (g.offset) this.#placeTrack(0, true);
    }

    this.#endGesture();
  }

  #endGesture() {
    this.#gesture = null;
    this.refs.dialog.classList.remove('is-gesturing', 'is-panning');
  }

  /**
   * @param {number} offset - Distance of the track from the resting position of the current photo.
   * @param {number} velocity - Finger speed at release (px/ms).
   * @param {number} moved - How far the finger moved sideways in this gesture.
   */
  #endSwipe(offset, velocity, moved) {
    const threshold = (this.#width || 1) * SWIPE_RATIO;
    let target = this.#index;

    if (Math.abs(velocity) > FLICK_SPEED) {
      // A quick flick in the direction the finger moved changes the photo. A flick back the
      // other way (the person changed their mind) keeps the current photo.
      if (velocity < 0 && moved < 0) target += 1;
      else if (velocity > 0 && moved > 0) target -= 1;
    } else if (offset < -threshold) target += 1;
    else if (offset > threshold) target -= 1;

    this.go(target, true);
  }

  /** @param {any} g */
  #endPan(g) {
    if (this.#scale > MAX_SCALE || this.#scale < 1.05) {
      this.#settleZoom(true, null);
      return;
    }

    const geom = g.geom || this.#geometry();
    if (!geom) return;

    const b = this.#bounds(this.#scale, geom);
    this.#tx = clamp(this.#tx + g.vx * 180, b.minX, b.maxX);
    this.#ty = clamp(this.#ty + g.vy * 180, b.minY, b.maxY);
    this.#applyZoom(true);
  }

  /**
   * Mouse: click the photo to zoom in or out, click beside it to close.
   * Touch: double tap to zoom in or out.
   * @param {PointerEvent} event
   * @param {any} g
   */
  #tap(event, g) {
    const x = event.clientX;
    const y = event.clientY;
    const geom = this.#geometry();

    if (g.pointer === 'mouse') {
      if (this.#scale > 1.01) this.#resetZoom(true);
      else if (geom && Math.abs(x - geom.cx) <= geom.w / 2 && Math.abs(y - geom.cy) <= geom.h / 2) {
        this.#zoomTo(ZOOM_SCALE, x, y);
      } else this.#close();
      return;
    }

    const last = this.#lastTap;
    if (last && event.timeStamp - last.t < DOUBLE_TAP_MS && Math.hypot(x - last.x, y - last.y) < DOUBLE_TAP_SLOP) {
      this.#lastTap = null;
      if (!geom) return;
      if (this.#scale > 1.01) this.#resetZoom(true);
      else this.#zoomTo(ZOOM_SCALE, x, y);
      return;
    }

    this.#lastTap = { t: event.timeStamp, x, y };
  }

  /* Wheel, buttons and keyboard */

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    event.preventDefault();
    if (!this.#isOpen || this.#gesture) return;

    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.#width || 800 : 1;
    const dx = event.deltaX * unit;
    const dy = event.deltaY * unit;

    // Trackpad pinch arrives as a wheel event with ctrlKey.
    if (event.ctrlKey) {
      if (!this.#geometry()) return;
      const scale = clamp(this.#scale * Math.exp(-dy * 0.01), 1, MAX_SCALE);
      if (scale <= 1.01) this.#resetZoom(false);
      else this.#zoomTo(scale, event.clientX, event.clientY, false);
      return;
    }

    if (this.#scale > 1.01) {
      const g = this.#geometry();
      if (!g) return;
      const b = this.#bounds(this.#scale, g);
      this.#tx = clamp(this.#tx - dx, b.minX, b.maxX);
      this.#ty = clamp(this.#ty - dy, b.minY, b.maxY);
      this.#applyZoom(false);
      return;
    }

    // One photo per scroll gesture.
    const now = event.timeStamp;
    if (now < this.#wheelUntil) {
      this.#wheelUntil = now + 220;
      return;
    }
    if (now - this.#wheelLast > 250) this.#wheelSum = 0;
    this.#wheelLast = now;
    this.#wheelSum += Math.abs(dx) > Math.abs(dy) ? dx : dy;
    if (Math.abs(this.#wheelSum) < 40) return;

    const direction = Math.sign(this.#wheelSum);
    this.#wheelSum = 0;
    this.#wheelUntil = now + 380;
    this.go(this.#index + direction);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(button instanceof HTMLElement) || !this.refs.dialog.contains(button)) return;
    if (button.getAttribute('aria-disabled') === 'true') return;

    switch (button.dataset.action) {
      case 'close':
        this.#close();
        break;
      case 'prev':
        this.go(this.#index - 1);
        break;
      case 'next':
        this.go(this.#index + 1);
        break;
      case 'zoom':
        this.#toggleZoom();
        break;
      case 'goto':
        this.go(Number(button.dataset.index) || 0);
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!this.#isOpen || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

    const zoomed = this.#scale > 1.01;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.#close();
        break;
      case 'ArrowRight':
      case 'ArrowLeft': {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        if (zoomed) this.#panBy(-direction, 0);
        else this.go(this.#index + direction);
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
        if (!zoomed) break;
        event.preventDefault();
        this.#panBy(0, event.key === 'ArrowDown' ? -1 : 1);
        break;
      case 'Home':
      case 'End':
        if (zoomed) break;
        event.preventDefault();
        this.go(event.key === 'Home' ? 0 : this.#count - 1);
        break;
      case '+':
      case '=':
        if (zoomed) break;
        event.preventDefault();
        this.#toggleZoom();
        break;
      case '-':
      case '_':
        if (!zoomed) break;
        event.preventDefault();
        this.#resetZoom(true);
        break;
      case 'Tab':
        this.#trapFocus(event);
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #trapFocus(event) {
    const { dialog } = this.refs;
    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter(
      (element) =>
        element instanceof HTMLElement &&
        !element.hidden &&
        !element.closest('[inert]') &&
        !element.matches(':disabled') &&
        element.getClientRects().length > 0
    );

    if (!items.length) {
      event.preventDefault();
      return;
    }

    const first = /** @type {HTMLElement} */ (items[0]);
    const last = /** @type {HTMLElement} */ (items[items.length - 1]);
    const active = document.activeElement;
    const outside = !active || active === dialog || !dialog.contains(active);

    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || (outside && active !== dialog))) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Timers */

  /**
   * @param {Function} callback
   * @param {number} delay
   */
  #later(callback, delay) {
    const id = window.setTimeout(() => {
      this.#timers.delete(id);
      callback();
    }, delay);
    this.#timers.add(id);
  }

  #clearTimers() {
    for (const id of this.#timers) window.clearTimeout(id);
    this.#timers.clear();
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Lets a dragged photo go a little past its limits, with resistance.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function rubber(value, min, max) {
  if (value < min) return min - (min - value) * 0.35;
  if (value > max) return max + (value - max) * 0.35;
  return value;
}

/**
 * @param {{x: number, y: number}} a
 * @param {{x: number, y: number}} b
 */
function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * @param {{x: number, y: number}} a
 * @param {{x: number, y: number}} b
 */
function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Resolves once test() is true, or after the timeout.
 * @param {() => boolean} test
 * @param {number} timeout
 */
function waitFor(test, timeout) {
  return new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      if (test() || performance.now() - start > timeout) resolve(undefined);
      else window.setTimeout(tick, 50);
    };
    tick();
  });
}

/** @param {Event} event */
function preventDefault(event) {
  if (event.cancelable) event.preventDefault();
}

/**
 * The element that opened the viewer, so focus can return to it.
 * @param {Event} [event]
 * @returns {HTMLElement | null}
 */
function findOpener(event) {
  const target = event?.target;
  if (target instanceof HTMLElement) {
    const opener = target.closest('button, a[href], slideshow-slide, [tabindex]');
    return opener instanceof HTMLElement ? opener : target;
  }

  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}
